import { memo, useCallback, useMemo, useState } from 'react'
import styled, { useTheme } from 'styled-components'
import { ComposedChart, XAxis, YAxis, ResponsiveContainer, Area, ReferenceDot } from 'recharts'

import { GetVaultMetricsHistoryIntervalType } from '@/shared/backend/types'

import { ChartTooltip } from 'components/ChartTooltip'

import { useSAVMetricsHistory } from '../hooks/useSAVMetricsHistory'
import { getDisplayTimestamp } from 'utils/helpers'
import { formatNumber } from './helpers'
import { addBoundaryNullsForGaps, getChartTicks, getGapThresholdForSavInterval } from 'utils/chartGapUtils'
import { ChartContentLoaderResponsive } from './ChartContentLoader'
import { Header } from './Header'
import { Typography } from 'components/Typography/Typography'
import { useChartScales } from './useChartScales'
import { useMinMaxDots } from './useMinMaxDots'
import { useIsMobile } from 'utils/useIsMobile'

const MOBILE_GRAPH_MARGIN = 30
const DESKTOP_GRAPH_MARGIN = 60
const DESKTOP_GRAPH_HEIGHT = 560
const DESKTOP_GRAPH_WIDTH = 1198
const MOBILE_PAGE_PADDING = 20
const ANIMATION_DURATION = 300
const MOBILE_TICK_COUNT = 2
const DESKTOP_TICK_COUNT = 5

const ChartsContainer = styled.div`
  padding-top: 16px;
  border-radius: 16px;
  border: 0.7px solid ${p => p.theme.colors.divider};
  background: ${p => p.theme.colors.background.primary};
  box-shadow: 0px 0px 16px 0px ${p => p.theme.colors.background.hover};
  margin-top: 32px;

  .brushFont {
    font-size: 11px !important;
  }
`

const EmptyDisplayMessage = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  fontsize: 16;
  fontweight: 500;
  pointer-events: none;
`

const MinMaxLabel = styled(Typography)<{ color: string }>`
  color: ${p => p.color};
  margin-right: 6px;
`

const MinMaxContainer = styled.div`
  display: flex;
  padding: 0 ${MOBILE_GRAPH_MARGIN}px;
  @media screen and (min-width: ${p => p.theme.breakpoints.md}px) {
    display: none;
  }
`

export const GraphTypes = {
  Apy: 'Apy',
  Tvl: 'Tvl',
}

interface Props {
  vaultId: string
}

export const ApyTvlCharts = memo(({ vaultId }: Props) => {
  const theme = useTheme()

  const [interval, setInterval] = useState<GetVaultMetricsHistoryIntervalType>('7d')
  const { data, isLoading } = useSAVMetricsHistory(vaultId, interval)

  const [filters, setFilters] = useState<Record<string, boolean>>({
    [GraphTypes.Apy]: true,
    [GraphTypes.Tvl]: true,
  })

  const isMobile = useIsMobile()

  const handleFilterClick = useCallback(
    (filterName: keyof typeof filters) => {
      const newFilters = {
        ...filters,
        [filterName]: !filters[filterName],
      }
      setFilters(newFilters)
    },
    [filters]
  )

  const rawChartData = useMemo(
    () =>
      data?.map(d => ({
        timestampMs: d.bucketTimestampMs,
        tvl: d.metrics.tvlUsdValue,
        apy: d.metrics.apy,
        label: getDisplayTimestamp(d.bucketTimestampMs),
      })) ?? [],
    [data]
  )

  const chartData = useMemo(
    () => addBoundaryNullsForGaps(rawChartData, ['tvl', 'apy'], getGapThresholdForSavInterval(interval)),
    [rawChartData, interval]
  )

  const ticks = useMemo(
    () => getChartTicks(rawChartData, isMobile ? MOBILE_TICK_COUNT : DESKTOP_TICK_COUNT),
    [rawChartData, isMobile]
  )

  const svgWidth = Math.min(window.innerWidth - MOBILE_PAGE_PADDING * 2, DESKTOP_GRAPH_WIDTH)
  const svgHeight = Math.min(window.innerHeight * 0.5, DESKTOP_GRAPH_HEIGHT)
  const marginLeft = isMobile ? MOBILE_GRAPH_MARGIN : DESKTOP_GRAPH_MARGIN
  const marginRight = isMobile ? MOBILE_GRAPH_MARGIN : DESKTOP_GRAPH_MARGIN

  const { xScale, yScales, xDomain, yDomains } = useChartScales({
    data: rawChartData,
    width: svgWidth,
    height: svgHeight,
    yKeys: ['tvl', 'apy'],
    marginLeft,
    marginRight,
  })

  const dots = useMinMaxDots({
    data: rawChartData,
    xScale,
    yScales,
    svgWidth,
    svgHeight,
    marginLeft,
    marginRight,
  })

  if (isLoading) {
    return (
      <ChartsContainer>
        <ChartContentLoaderResponsive />
      </ChartsContainer>
    )
  }

  if (!data || data.length === 0) {
    return (
      <ChartsContainer style={{ position: 'relative' }}>
        <Header
          interval={interval}
          setInterval={setInterval}
          filters={filters}
          handleFilterClick={handleFilterClick}
          disabled
        />
        <ResponsiveContainer width="100%" height={svgHeight}>
          <ComposedChart data={[]} barCategoryGap={0} barGap={0}>
            {/* empty */}
          </ComposedChart>
        </ResponsiveContainer>
        <EmptyDisplayMessage>
          <Typography variant="label2" fontWeight="semiBold" color="secondary">
            No events to display
          </Typography>
        </EmptyDisplayMessage>
      </ChartsContainer>
    )
  }

  return (
    <ChartsContainer>
      <Header interval={interval} setInterval={setInterval} filters={filters} handleFilterClick={handleFilterClick} />
      {dots.length > 0 && (
        <MinMaxContainer>
          {dots.map((d, i) => (
            <MinMaxLabel key={i} variant="caption2" color={d.color}>
              {`${d.label}: ${d.value}`}
            </MinMaxLabel>
          ))}
        </MinMaxContainer>
      )}
      <ResponsiveContainer width="100%" height={svgHeight}>
        <ComposedChart
          data={chartData}
          barCategoryGap={0}
          barGap={0}
          margin={{
            left: marginLeft,
            right: marginRight,
          }}
        >
          <XAxis
            dataKey="timestampMs"
            type="number"
            tickFormatter={value => getDisplayTimestamp(value)}
            height={50}
            tick={{ fontSize: isMobile ? 12 : 14, fill: theme.colors.text.primary }}
            axisLine={{ stroke: theme.colors.text.primaryDark }}
            domain={xDomain}
            ticks={ticks}
            interval={0}
            tickLine={{
              stroke: theme.colors.text.primaryDark,
              strokeWidth: 2,
            }}
          />
          <YAxis hide yAxisId="left" domain={filters[GraphTypes.Tvl] ? yDomains.tvl : []} />
          <YAxis hide yAxisId="right" orientation="right" domain={filters[GraphTypes.Apy] ? yDomains.apy : []} />
          <ChartTooltip
            contentStyle={{ backgroundColor: theme.colors.background.primary, border: 'none' }}
            labelFormatter={(label: number) => getDisplayTimestamp(label)}
            formatter={(value, name: string) => {
              if (value === null || value === undefined) {
                return null
              }
              const numValue = value as number
              if (name === 'APY') {
                return [`${(numValue * 100).toFixed(2)}%`, 'APY']
              }
              if (name === 'TVL') {
                return [formatNumber(numValue), 'TVL']
              }
              return [numValue, name]
            }}
          />
          <defs>
            <linearGradient id="tvlGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={theme.colors.button.background} stopOpacity={0.7} />
              <stop offset="100%" stopColor={theme.colors.button.background} stopOpacity={0} />
            </linearGradient>
          </defs>
          {filters[GraphTypes.Tvl] && (
            <Area
              type="monotone"
              yAxisId="left"
              dataKey="tvl"
              name="TVL"
              fill="url(#tvlGradient)"
              stroke={theme.colors.button.background}
              animationDuration={ANIMATION_DURATION}
            />
          )}
          {filters[GraphTypes.Apy] && (
            <Area
              yAxisId="right"
              type="monotone"
              dataKey="apy"
              name="APY"
              stroke={theme.colors.text.danger2}
              fill="transparent"
              dot={false}
              strokeWidth={2}
              animationDuration={ANIMATION_DURATION}
            />
          )}
          {dots.map((dot, i) => (
            <ReferenceDot
              key={i}
              x={dot.entry.timestampMs}
              y={(dot.yAxisId === 'left' ? dot.entry.tvl : dot.entry.apy) ?? undefined}
              yAxisId={dot.yAxisId}
              r={4}
              fill={dot.color}
              stroke={dot.color}
              label={
                isMobile
                  ? undefined
                  : {
                      position: dot.position,
                      dx: dot.dx ?? 0,
                      dy: dot.dy ?? 0,
                      value: `${dot.label}: ${dot.value}`,
                      fontSize: 14,
                      fill: dot.color,
                    }
              }
            />
          ))}
        </ComposedChart>
      </ResponsiveContainer>
    </ChartsContainer>
  )
})
